import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollContainer, getScrollTop, scrollTo } from '@theme/scroll-container';

let openedInDocument = false;
const DAY = 86400000;

class IhhNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.teaser = this.querySelector('[data-popup-open]');
    this.invitation = this.querySelector('[data-popup-invitation]');
    this.inviteReady = false;
    this.desktop = window.matchMedia('(min-width: 751px)');
    this.response = this.querySelector('[data-popup-response]').dataset.popupResponse;
    this.editor = this.dataset.editor === 'true';
    this.key = `ihh-newsletter:${this.dataset.themeId}:v1`;
    this.options = { signal: this.controller.signal };
    this.migrateSubscription();
    this.teaser?.addEventListener('click', () => this.open(), this.options);
    this.querySelector('[data-popup-invitation-close]')?.addEventListener('click', () => {
      this.inviteReady = false;
      this.syncTeaser();
      if (this.previousFocus?.isConnected && this.previousFocus.getClientRects().length) this.previousFocus.focus({ preventScroll: true });
    }, this.options);
    this.desktop.addEventListener('change', () => {
      if (!this.desktop.matches) this.inviteReady = false;
      this.syncTeaser();
    }, this.options);
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const opener = event.target.closest('[data-newsletter-popup-open], a[href="#newsletter"]');
      if (!opener || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open();
    }, this.options);
    this.querySelector('[data-popup-close]').addEventListener('click', () => this.dialog.close(), this.options);
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dialog.close();
    }, this.options);
    this.dialog.addEventListener('close', () => this.onClose(), this.options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
    }, this.options);
    this.dialog.addEventListener('keydown', (event) => this.trapFocus(event), this.options);
    this.form.addEventListener('submit', (event) => {
      if (!this.form.checkValidity()) {
        event.preventDefault();
        this.form.reportValidity();
        return;
      }
      this.write('sessionStorage', 'pending', Date.now());
      // Preserve the exact path, filters and fragment without storing form values.
      this.write('sessionStorage', 'returnContext', JSON.stringify({
        url: location.pathname + location.search + location.hash,
        scroll: this.dialog.open ? this.previousScroll : getScrollTop(),
        time: Date.now(),
      }));
    }, this.options);
    this.querySelector('[data-popup-continue]')?.addEventListener('click', () => this.dialog.close(), this.options);
    this.querySelector('input[type=email]').addEventListener('input', (event) => event.target.removeAttribute('aria-invalid'), this.options);
    window.addEventListener('pageshow', () => {
      this.syncTeaser();
      requestAnimationFrame(() => this.restoreDialogFocus());
    }, this.options);
    window.addEventListener('load', () => this.restoreDialogFocus(), this.options);
    window.addEventListener('storage', () => {
      if (this.suppressed()) this.stopAutomatic();
      this.syncTeaser();
    }, this.options);
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) this.open();
    }, this.options);
    document.addEventListener('shopify:block:select', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) this.open();
    }, this.options);
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail.sectionId === this.dataset.sectionId && this.dialog.open) this.dialog.close();
    }, this.options);

    const pending = Number(this.read('sessionStorage', 'pending'));
    const isReturn = location.hash === `#${this.form.id}` || (pending > 0 && Date.now() - pending < 30 * 60 * 1000);
    if (isReturn && (this.response === 'success' || this.response === 'error')) this.restoreReturnContext();

    const returnTo = this.form.elements.namedItem('return_to');
    const returnUrl = new URL(location.href);
    returnUrl.searchParams.delete('newsletter_popup');
    returnUrl.searchParams.set('customer_posted', 'true');
    returnUrl.hash = this.form.id;
    returnTo.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;

    if (this.response === 'success' && isReturn) {
      if (!this.editor) {
        this.write('localStorage', 'subscribedAt', Date.now());
        this.write('localStorage', 'subscribedUntil', Date.now() + Number(this.dataset.subscribedDays) * DAY);
      }
      this.showSuccess();
      this.open();
      this.write('sessionStorage', 'pending', 0);
    } else if (this.response === 'error' && isReturn) {
      this.open();
      this.querySelector('[data-popup-error]')?.focus({ preventScroll: true });
      this.write('sessionStorage', 'pending', 0);
    }
    if (this.returnScroll !== undefined) {
      this.previousScroll = this.returnScroll;
      // Restore the background after native fragment scrolling on form return.
      const restoreScroll = () => scrollTo({ top: this.returnScroll, behavior: 'instant' });
      if (document.readyState === 'complete') requestAnimationFrame(restoreScroll);
      else window.addEventListener('load', restoreScroll, { ...this.options, once: true });
    }
    this.syncTeaser();

    if (this.editor || this.dialog.open) return;
    if (new URLSearchParams(location.search).get('newsletter_popup') === 'open' || location.hash === '#newsletter') {
      this.open();
      return;
    }
    this.startAutomatic();
  }

  disconnectedCallback() {
    this.stopAutomatic();
    this.controller.abort();
    if (this.dialog.open) this.dialog.close();
    unlockScroll(this.dialog);
  }

  read(storage, suffix) {
    try { return window[storage].getItem(`${this.key}:${suffix}`) || 0; } catch { return this[suffix] || 0; }
  }

  write(storage, suffix, value) {
    this[suffix] = value;
    try { window[storage].setItem(`${this.key}:${suffix}`, String(value)); } catch {}
  }

  restoreReturnContext() {
    let context;
    try { context = JSON.parse(this.read('sessionStorage', 'returnContext')); } catch {}
    let url;
    if (context && Date.now() - context.time >= 0 && Date.now() - context.time < 30 * 60 * 1000) {
      try {
        const candidate = new URL(context.url, location.origin);
        if (candidate.origin === location.origin && candidate.pathname === location.pathname) {
          url = context.url;
          if (Number.isFinite(context.scroll) && context.scroll >= 0) this.returnScroll = context.scroll;
        }
      } catch {}
    }
    if (!url) {
      // Storage may be unavailable. Remove only our own return markers.
      const fallback = new URL(location.href);
      fallback.searchParams.delete('customer_posted');
      if (fallback.hash === `#${this.form.id}`) fallback.hash = '';
      url = fallback.pathname + fallback.search + fallback.hash;
    }
    try { history.replaceState(history.state, '', url); } catch {}
    this.write('sessionStorage', 'returnContext', '');
  }

  migrateSubscription() {
    const until = Number(this.read('localStorage', 'subscribedUntil'));
    if (until > 0 && !Number(this.read('localStorage', 'subscribedAt'))) {
      const subscribedAt = until - 180 * DAY;
      this.write('localStorage', 'subscribedAt', subscribedAt);
      this.write('localStorage', 'subscribedUntil', subscribedAt + Number(this.dataset.subscribedDays) * DAY);
    }
  }

  suppressed() {
    return Number(this.read('localStorage', 'subscribedUntil')) > Date.now() ||
      Number(this.read('localStorage', 'dismissedUntil')) > Date.now();
  }

  syncTeaser() {
    if (!this.invitation) return;
    this.invitation.hidden = !this.inviteReady || this.dialog.open || !this.desktop.matches ||
      this.editor || this.successShown || this.dataset.autoEligible !== 'true' ||
      Number(this.read('localStorage', 'subscribedUntil')) > Date.now();
  }

  autoEligible() {
    return this.isConnected && !this.editor && !openedInDocument &&
      this.dataset.autoOpen === 'true' && this.dataset.autoEligible === 'true' && !this.suppressed();
  }

  startAutomatic() {
    if (!this.autoEligible()) return;
    this.autoController = new AbortController();
    const options = { signal: this.autoController.signal };
    this.timer = setTimeout(() => this.tryAutoOpen(), Number(this.dataset.delay) * 1000);
    if (this.dataset.scrollTrigger === 'true') {
      document.addEventListener('scroll', (event) => {
        const container = getScrollContainer();
        if (event.target !== container && event.target !== document) return;
        const maxScroll = container.scrollHeight - container.clientHeight;
        if (maxScroll > 0 && container.scrollTop / maxScroll * 100 >= Number(this.dataset.scrollPercent)) this.tryAutoOpen();
      }, { ...options, capture: true, passive: true });
    }
    if (this.dataset.exitIntent === 'true') {
      document.addEventListener('mouseout', (event) => {
        if (!event.relatedTarget && event.clientY <= 0 && window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) this.tryAutoOpen();
      }, options);
    }
  }

  stopAutomatic() {
    clearTimeout(this.timer);
    clearTimeout(this.retryTimer);
    this.autoController?.abort();
  }

  hasOpenOverlay(root = document) {
    const overlays = root.querySelectorAll('dialog[open], [aria-modal="true"], [popover]:popover-open, #shopify-pc__banner');
    if ([...overlays].some((el) => el !== this.dialog && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden')) return true;
    return [...root.querySelectorAll('*')].some((el) => el.shadowRoot && this.hasOpenOverlay(el.shadowRoot));
  }

  tryAutoOpen() {
    if (!this.autoEligible() || this.dialog.open) {
      this.stopAutomatic();
      return;
    }
    const busy = document.hidden || document.documentElement.hasAttribute('scroll-lock') ||
      document.activeElement?.matches('input, textarea, select, [contenteditable="true"]') ||
      this.hasOpenOverlay();
    if (busy) {
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.tryAutoOpen(), 2000);
      return;
    }
    this.open();
  }

  open() {
    if (this.dialog.open) return;
    openedInDocument = true;
    this.stopAutomatic();
    this.previousFocus = document.activeElement;
    this.previousScroll = getScrollTop();
    if (this.invitation && this.desktop.matches) this.invitation.querySelector('img').loading = 'eager';
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    lockScroll(this.dialog);
    this.syncTeaser();
    const target = this.successShown ? this.querySelector('[data-popup-success]') :
      this.querySelector('[data-popup-error]') || this.querySelector('.ihh-newsletter__title');
    target?.focus({ preventScroll: true });
  }

  onClose() {
    unlockScroll(this.dialog);
    if (!this.editor) this.write('localStorage', 'dismissedUntil', Date.now() + Number(this.dataset.dismissDays) * DAY);
    this.inviteReady = this.desktop.matches;
    this.syncTeaser();
    if (this.invitation && !this.invitation.hidden) this.teaser.focus({ preventScroll: true });
    else if (this.previousFocus?.isConnected && this.previousFocus !== document.body && this.previousFocus.getClientRects().length) this.previousFocus.focus({ preventScroll: true });
    scrollTo({ top: this.previousScroll, behavior: 'instant' });
  }

  showSuccess() {
    const success = this.querySelector('[data-popup-success]');
    if (!success) return;
    this.querySelector('[data-popup-signup]').hidden = true;
    success.hidden = false;
    this.successShown = true;
    this.dialog.setAttribute('aria-labelledby', success.querySelector('h2').id);
  }

  restoreDialogFocus() {
    if (!this.dialog.open || this.dialog.contains(document.activeElement)) return;
    const target = this.successShown ? this.querySelector('[data-popup-success]') :
      this.querySelector('[data-popup-error]') || this.querySelector('.ihh-newsletter__title');
    target?.focus({ preventScroll: true });
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const controls = [...this.dialog.querySelectorAll('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex="0"]')]
      .filter((el) => !el.disabled && !el.closest('[hidden], [inert]') && el.getClientRects().length);
    if (!controls.length) return;
    const current = controls.indexOf(document.activeElement);
    const next = current < 0 ? (event.shiftKey ? controls.length - 1 : 0) :
      (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
    event.preventDefault();
    controls[next].focus();
  }
}

if (!customElements.get('ihh-newsletter-popup')) customElements.define('ihh-newsletter-popup', IhhNewsletterPopup);
